import React, {useEffect, useMemo, useState} from 'react';
import {useUploadFileMutation} from '../../store/builder/inspections.api';
import {setFields, setLoader} from '../../store/builder/reducer';
import {useDispatch} from 'react-redux';
import {useParams} from 'react-router-dom';
import useLoader from '../../global/UseLoadingHandler';
import Gallery from '../Gallery';

const FillPhoto = ({
  inspection_id,
  item,
  readOnly,
  category_id,
  disabled,
  type,
}) => {
  const dispatch = useDispatch();
  const params = useParams();
  const [images, setImages] = useState([]);
  const [imageGallery, setImageGallery] = useState(null);
  const [uploadFile, {isSuccess, isLoading, isError, error}] =
    useUploadFileMutation();

  let guid = () => {
    let s4 = () => {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    };
    return (
      s4() +
      s4() +
      '-' +
      s4() +
      '-' +
      s4() +
      '-' +
      s4() +
      '-' +
      s4() +
      s4() +
      s4()
    );
  };

  useEffect(() => {
    if (type === 'single') {
      if (item?.photo != null && item?.photo !== undefined) {
        setImages([{...item.photo, preview: item.photo?.url || item.photo?.preview}]);
      } else if (!item?.answers?.length) {
        setImages([]);
      }
    } else if (type === 'multiple') {
      if (item?.photos?.length > 0) {
        setImages(item.photos.map(el => ({...el, preview: el?.url || el?.preview})));
      } else if (!item?.answers?.length) {
        setImages([]);
      }
    }
  }, [item, type]);

  useLoader([isLoading]);

  const handleRemoveImage = indexToRemove => {
    const newImages = images.filter((_, index) => index !== indexToRemove);
    setImages(newImages);
    const remainingAnswers = newImages.map(
      img => img.uuid || img.id,
    ).filter(Boolean);
    const payload = {
      id: item.id,
      answers: remainingAnswers,
      has_comment: false,
      comment_text: '',
      comment_files: [],
    };
    if (type === 'single') {
      payload.photo = remainingAnswers.length ? undefined : null;
    } else {
      payload.photos = newImages.map(img => ({
        uuid: img.uuid,
        url: img.url || img.preview,
        filename: img.filename || img.data?.name || '',
      }));
    }
    dispatch(setFields(payload));
  };

  const handleImage = e => {
    const files = Array.from(e.target.files);
    const isSingle = type === 'single';

    const selectedFiles = isSingle
      ? files.slice(0, 1)
      : files.slice(0, 5 - images.length);
    const newImages = selectedFiles.map(file => ({
      preview: URL.createObjectURL(file),
      data: file,
      type: file.type === 'application/pdf' ? 'pdf' : 'image',
      uuid: guid(),
    }));

    setImages(isSingle ? newImages : prev => [...prev, ...newImages]);

    const existingAnswers = isSingle ? [] : (item?.answers || []);
    const existingPhotos = isSingle ? [] : (item?.photos || []);

    const uploadPromises = selectedFiles.map((file, index) => {
      const form = new FormData();
      form.append('file', file);
      form.append('type', 'inspection');
      form.append('inspection_id', params?.id);
      form.append('category_id', category_id);
      form.append('uuid', newImages[index].uuid);
      return uploadFile(form).then(res => ({
        index,
        url: res?.data?.url ?? res?.data?.data?.url ?? newImages[index].preview,
        filename: file.name,
      }));
    });

    Promise.all(uploadPromises).then(results => {
      const newAnswers = isSingle
        ? newImages.map(img => img.uuid)
        : [...existingAnswers, ...newImages.map(img => img.uuid)];
      const photoPayloads = newImages.map((img, i) => {
        const result = results.find(r => r.index === i);
        return {
          uuid: img.uuid,
          url: result?.url ?? img.preview,
          filename: result?.filename ?? img.data?.name ?? '',
        };
      });
      dispatch(
        setFields({
          id: item.id,
          answers: newAnswers,
          has_comment: false,
          comment_text: '',
          comment_files: [],
          photo: isSingle ? photoPayloads[0] : undefined,
          photos: isSingle ? undefined : [...existingPhotos, ...photoPayloads],
        }),
      );
    });
  };

  const canRemove = !readOnly && !disabled;

  const renderImagePreview = useMemo(() => {
    return images?.map((img, index) => (
      <div
        key={img.uuid || img.id || index}
        className='file__preview-inspectionPhotoWrapper'
      >
        {img.type === 'pdf' ? (
          <a
            href={img.preview}
            target='_blank'
            rel='noreferrer'
            className='file__preview-inspectionPhoto'
          >
            {img.data?.name}
          </a>
        ) : img.preview ? (
          <button
            type='button'
            className='file__preview-inspectionPhotoTrigger'
            onClick={() => {
              const urls = images
                .filter(
                  g => g.type !== 'pdf' && (g.preview || g.url),
                )
                .map(g => g.preview || g.url);
              const active = img.preview || img.url;
              setImageGallery({urls, active});
            }}
            aria-label='View full size image'
          >
            <img
              src={img.preview}
              alt='preview'
              className='file__preview-inspectionPhoto'
            />
          </button>
        ) : null}
        {canRemove && (
          <button
            type='button'
            className='file__preview-remove'
            onClick={() => handleRemoveImage(index)}
            aria-label='Remove'
          >
            ×
          </button>
        )}
      </div>
    ));
  }, [images, canRemove]);

  return (
    <div className='equipment__file'>
      {imageGallery && (
        <Gallery
          images={imageGallery.urls}
          chosenPhoto={imageGallery.active}
          exit={() => setImageGallery(null)}
          showDownload={false}
          showThumbnails={false}
        />
      )}
      <div className='file'>
        <input
          id={item?.id}
          type='file'
          accept='image/png, image/jpeg, .pdf'
          onChange={handleImage}
          disabled={
            readOnly || disabled || (type === 'multiple' && images.length >= 5)
          }
          multiple={type === 'multiple'}
        />
        {!images.length && !item?.photo?.url ? (
          <label htmlFor={item?.id} className='file__label'>
            <img
              className='file__preview-choose'
              src='/assets/icons/camera.svg'
              alt='camera'
            />
            <p>Choose file</p>
          </label>
        ) : (
          <div className='file__preview column'>
            <div className='file__preview-inspectionPhotoContainer'>
              {renderImagePreview}
            </div>
            <label htmlFor={item?.id} className='file__label'>
              <img
                className='file__preview-choose'
                src='/assets/icons/camera.svg'
                alt='camera'
              />
              <p>{images.length > 0 ? 'Choose another file' : 'Choose file'}</p>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default FillPhoto;
